import React from "react";
import { Icon } from "./Icon.jsx";

/* ============================================================
   Tokeniser Button — aligned to Figma Button spec.
   Variants (semantic tokens):
     primary      — fill general/primary, text primary-foreground
     secondary    — fill general/secondary, text secondary-foreground
     outline      — fill unofficial/outline, stroke border-3, text foreground
     ghost        — fill unofficial/ghost, text ghost-foreground
     destructive  — fill general/destructive, text destructive-foreground
   Sizes (6): xs 24 · sm 28 · md 32 · lg 36 · xl 40 · 2xl 48
   Radius: rounded-full (pill).
   Shadow: outline / ghost / destructive get shadow-xs.
   Hover deepens fill; press nudges 1px; disabled = 0.5 opacity.
   Legacy: variant="tertiary" → ghost, variant="danger" → destructive.
   ============================================================ */

const SIZES = {
  xs:  { height: 24, padding: "0 10px", font: 14, gap: 4,  icon: 12 },
  sm:  { height: 28, padding: "0 12px", font: 14, gap: 6,  icon: 14 },
  md:  { height: 32, padding: "0 14px", font: 14, gap: 6,  icon: 14 },
  lg:  { height: 36, padding: "0 16px", font: 16, gap: 8,  icon: 16 },
  xl:  { height: 40, padding: "0 18px", font: 16, gap: 8,  icon: 18 },
  "2xl": { height: 48, padding: "0 22px", font: 16, gap: 10, icon: 20 },
};

function normalizeVariant(v) {
  if (v === "tertiary") return "ghost";
  if (v === "danger") return "destructive";
  return v || "primary";
}

function normalizeSize(s) {
  return SIZES[s] ? s : "lg";
}

function variantStyle(variant, hover) {
  switch (variant) {
    case "secondary":
      return {
        background: hover ? "var(--secondary-hover)" : "var(--secondary)",
        color: "var(--secondary-foreground)",
        boxShadow: "none",
      };
    case "outline":
      return {
        background: hover ? "var(--outline-hover)" : "var(--outline)",
        color: "var(--foreground)",
        boxShadow: "inset 0 0 0 1px var(--border-3), var(--shadow-xs)",
      };
    case "ghost":
      return {
        background: hover ? "var(--ghost-hover)" : "var(--ghost)",
        color: "var(--ghost-foreground)",
        boxShadow: "var(--shadow-xs)",
      };
    case "destructive":
      return {
        background: hover ? "var(--red-500)" : "var(--destructive)",
        color: "var(--destructive-foreground)",
        boxShadow: "var(--shadow-xs)",
      };
    case "primary":
    default:
      return {
        background: hover ? "var(--primary-hover)" : "var(--primary)",
        color: "var(--primary-foreground)",
        boxShadow: "none",
      };
  }
}

export function Button({
  children,
  variant = "primary",
  size = "lg",
  iconLeft,
  iconRight,
  fullWidth = false,
  disabled = false,
  type = "button",
  onClick,
  style,
  ...rest
}) {
  const [hover, setHover] = React.useState(false);
  const [active, setActive] = React.useState(false);
  const v = normalizeVariant(variant);
  const s = SIZES[normalizeSize(size)];
  const vs = variantStyle(v, hover && !disabled);

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      style={{
        display: fullWidth ? "flex" : "inline-flex",
        width: fullWidth ? "100%" : undefined,
        alignItems: "center",
        justifyContent: "center",
        gap: s.gap,
        height: s.height,
        padding: s.padding,
        border: "none",
        borderRadius: "var(--rounded-full)",
        fontFamily: "var(--font-family-text)",
        fontWeight: "var(--paragraph-medium-weight)",
        fontSize: s.font,
        lineHeight: 1,
        letterSpacing: 0,
        whiteSpace: "nowrap",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        transition:
          "background var(--dur) var(--ease), box-shadow var(--dur) var(--ease), transform var(--dur-fast) var(--ease), opacity var(--dur) var(--ease)",
        transform: active && !disabled ? "translateY(1px)" : "none",
        ...vs,
        ...style,
      }}
      {...rest}
    >
      {iconLeft ? <Icon name={iconLeft} size={s.icon} /> : null}
      {children ? <span>{children}</span> : null}
      {iconRight ? <Icon name={iconRight} size={s.icon} /> : null}
    </button>
  );
}

export default Button;
